import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils, EMPTY_PROJECT_DETAIL } from '../../utils/commonUtils';
import { apiDateTimeToDatetimeLocalValueWithSeconds, datetimeLocalToIsoUtc, formatDateTimeFromApi } from '../../utils/dateTime';
import { getVoucherDetail, updateVoucher, createVoucherFile, deleteVoucherPrimary, resolveVoucherPrimaryUrl, toVoucherPrimaryFileRequest, VoucherDetailResponse } from '../../utils/voucher';
import { FormData, FormErrors, VoucherFormUtils, VoucherStatus, VoucherType, DiscountTarget, DiscountType, ValidType } from '../../utils/voucherCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { getCategoryGroupDetail, updateVoucherInTree, findPathToNode, CategoryGroupTreeNode } from '../../utils/voucherCategoryGroup';
import {
  UploadFilePayload,
  toUploadFilePayload,
  uploadSingleFileViaPresign,
} from '../../utils/storage';
import VoucherPreviewCard, {
  formatVoucherPreviewBenefit,
  formatVoucherPreviewValidity,
} from './components/VoucherPreviewCard';
import { 
  Save,
  ArrowLeft,
  X,
  AlertCircle,
  Info,
  DollarSign,
  Gift,
  FileText,
  RefreshCw,
  History,
  Calendar,
  Users,
  Percent,
  Trash2
} from 'lucide-react';

const VoucherUpdate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const { voucherCode } = useParams<{ voucherCode: string }>();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('voucher-list');
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(VoucherFormUtils.getDefaultFormData());
  const [errors, setErrors] = useState<FormErrors>({});
  const [originalData, setOriginalData] = useState<FormData | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [voucherMetadata, setVoucherMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // 대표 이미지 (프로젝트 수정과 동일 패턴)
  const [uploadFiles, setUploadFiles] = useState<UploadFilePayload[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [originalExistingImageUrl, setOriginalExistingImageUrl] = useState<string | null>(null);
  const [originalHadImage, setOriginalHadImage] = useState(false);
  const [pendingImageDelete, setPendingImageDelete] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string>('');

  // 카테고리/그룹 (treeCodes 기반 브레드크럼)
  const [treeCodes, setTreeCodes] = useState<string[]>([]);
  const [categoryBreadcrumbs, setCategoryBreadcrumbs] = useState<Array<{ treeCode: string; groupCode: string; pathTitles: string[] }>>([]);
  const [isLoadingCategoryGroups, setIsLoadingCategoryGroups] = useState(false);
  const [removingTreeCode, setRemovingTreeCode] = useState<string | null>(null);

  // Check if screen is mobile size
  VoucherFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = VoucherFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) return;
      try {
        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) return;
        setIsLoadingBrands(true);
        const response = await getBrandListFlat(projectCode);
        if (response?.brands) setBrands(response.brands);
        else setBrands([]);
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };
    if (projectDetail) fetchBrands();
  }, [projectDetail]);

  // 교환권 상세 정보 로드 (중복 호출 방지)
  const isFetchingRef = React.useRef(false);
  
  useEffect(() => {
    const loadVoucherDetail = async () => {
      if (!voucherCode) {
        setLoadError('교환권 코드가 없습니다.');
        setIsLoading(false);
        return;
      }
      
      // 이미 요청 중이면 중복 호출 방지
      if (isFetchingRef.current) {
        return;
      }
      
      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        setLoadError(null);
        
        const projectCode = getProjectCode();
        const voucher: VoucherDetailResponse = await getVoucherDetail(projectCode, voucherCode);

        // API 응답을 FormData 형태로 변환
        const voucherFormData: FormData = {
          brandCode: (voucher as { brandCode?: string }).brandCode || '',
          productCode: voucher.productCode || '',
          masterCode: voucher.masterCode ?? '',
          status: voucher.status as VoucherStatus,
          type: (voucher.type === 'EVENT' ? 'EVENT' : 'COUPON') as VoucherType,
          name: voucher.name,
          price: voucher.price || 0,
          balance: voucher.balance || 0,
          discountTarget: (voucher.discountTarget || 'PRODUCT') as DiscountTarget,
          discountType: (voucher.discountType || 'AMOUNT') as DiscountType,
          discountValue: voucher.discountValue || 0,
          maxDiscount: voucher.maxDiscount || 0,
          minPayment: voucher.minPayment || 0,
          validType: (voucher.validType || 0) as ValidType,
          validPeriod: voucher.validPeriod || 0,
          validUntil:
            voucher.validType === 1 && voucher.validUntil
              ? apiDateTimeToDatetimeLocalValueWithSeconds(voucher.validUntil)
              : '',
          issueLimit: voucher.issueLimit || 0,
          memberLimit: voucher.memberLimit || 0,
          comment: voucher.comment || '',
          description: voucher.description || '',
          notice: voucher.notice || ''
        };

        setFormData(voucherFormData);
        setOriginalData({ ...voucherFormData });

        const projectCodeForImage = getProjectCode();
        const resolvedImage = await resolveVoucherPrimaryUrl(
          projectCodeForImage,
          voucherCode,
          voucher.primaryVersion,
          voucher.updatedAt
        ).catch((err) => {
          console.warn('교환권 대표 이미지 URL 조회 실패:', voucherCode, err);
          return null;
        });
        setExistingImageUrl(resolvedImage);
        setOriginalExistingImageUrl(resolvedImage);
        setOriginalHadImage(!!resolvedImage);
        setPendingImageDelete(false);
        setUploadFiles([]);
        setImagePreview('');
        setImageError('');
        
        // Set voucher metadata
        setVoucherMetadata({
          createdAt: voucher.createdAt || '',
          createdBy: voucher.createdBy || '',
          updatedAt: voucher.updatedAt || '',
          updatedBy: voucher.updatedBy || ''
        });
        
        // treeCodes로 카테고리/그룹 브레드크럼 구성
        const rawTreeCodes: string[] = voucher.treeCodes ?? [];
        setTreeCodes(rawTreeCodes);
        if (rawTreeCodes.length === 0) {
          setCategoryBreadcrumbs([]);
          setIsLoadingCategoryGroups(false);
        } else {
          setIsLoadingCategoryGroups(true);
          const list: Array<{ treeCode: string; groupCode: string; pathTitles: string[] }> = [];
          for (const tc of rawTreeCodes) {
            const colonIdx = tc.indexOf(':');
            const groupCode = colonIdx >= 0 ? tc.slice(0, colonIdx) : tc;
            const nodeCode = colonIdx >= 0 ? tc.slice(colonIdx + 1) : '';
            try {
              const detail = await getCategoryGroupDetail(projectCode, groupCode);
              const tree = (detail as { tree?: CategoryGroupTreeNode[] }).tree;
              const pathTitles =
                (tree?.length && nodeCode ? findPathToNode(tree, nodeCode) : null) ?? [detail.title || groupCode, nodeCode || tc];
              list.push({ treeCode: tc, groupCode, pathTitles });
            } catch {
              list.push({ treeCode: tc, groupCode, pathTitles: [groupCode, nodeCode || tc] });
            }
          }
          setCategoryBreadcrumbs(list);
          setIsLoadingCategoryGroups(false);
        }
      } catch (error: any) {
        console.error('교환권 정보 로드 실패:', error);
        const displayMessage = CommonUtils.getNetworkErrorMessage(error);
        setLoadError(displayMessage);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    loadVoucherDetail();
  }, [voucherCode]);

  // Check for changes
  useEffect(() => {
    if (!originalData) {
      setHasChanges(false);
      return;
    }
    const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    const hasFileChanges = uploadFiles.length > 0 || pendingImageDelete;
    setHasChanges(hasFormChanges || hasFileChanges);
  }, [formData, originalData, uploadFiles, pendingImageDelete]);

  const displayImageUrl =
    pendingImageDelete && uploadFiles.length === 0
      ? null
      : imagePreview || existingImageUrl;

  const handleValidatedImage = async ({ file, dataUrl }: { file: File; dataUrl: string }) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setImageError('프로젝트 정보가 없습니다.');
      return;
    }

    setUploadFiles([]);
    setImagePreview('');
    setExistingImageUrl(null);
    setImageError('');
    setIsImageUploading(true);

    try {
      const { upload } = await uploadSingleFileViaPresign(file, {
        domain: 'VOUCHER',
        projectCode,
      });
      setUploadFiles([toUploadFilePayload(upload)]);
      setImagePreview(dataUrl);
      setPendingImageDelete(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다';
      setImageError(message);
      setExistingImageUrl(originalExistingImageUrl);
      setImagePreview('');
      setUploadFiles([]);
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleImageLoadError = () => {
    // 파일 없거나 URL 무효 — 깨진 이미지 대신 업로드 영역 표시
    setExistingImageUrl(null);
    setOriginalExistingImageUrl(null);
    setOriginalHadImage(false);
    setImagePreview('');
    setPendingImageDelete(false);
    setUploadFiles([]);
  };

  const handleRemoveImage = () => {
    if (uploadFiles.length > 0) {
      setUploadFiles([]);
      setImagePreview('');
      if (pendingImageDelete || !originalHadImage) {
        setExistingImageUrl(null);
      } else {
        setExistingImageUrl(originalExistingImageUrl);
        setPendingImageDelete(false);
      }
      setImageError('');
      return;
    }

    if (originalHadImage && !pendingImageDelete) {
      setPendingImageDelete(true);
      setExistingImageUrl(null);
      setImagePreview('');
      setImageError('');
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    VoucherFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // 카테고리/그룹 트리에서 교환권 제거
  const handleRemoveFromTree = async (treeCode: string) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode || !voucherCode) return;
    try {
      setRemovingTreeCode(treeCode);
      await updateVoucherInTree(projectCode, voucherCode, { operation: 'remove', treeCode });
      setTreeCodes((prev) => prev.filter((t) => t !== treeCode));
      setCategoryBreadcrumbs((prev) => prev.filter((b) => b.treeCode !== treeCode));
    } catch (err) {
      console.error('트리에서 제거 실패:', err);
      alert(err instanceof Error ? err.message : '제거에 실패했습니다.');
    } finally {
      setRemovingTreeCode(null);
    }
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors = VoucherFormUtils.validateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isImageUploading) {
      alert('이미지 업로드가 진행 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const hasFormChanges =
      !!originalData && JSON.stringify(formData) !== JSON.stringify(originalData);
    const hasFileChanges = uploadFiles.length > 0 || pendingImageDelete;

    if (!hasFormChanges && !hasFileChanges) {
      return;
    }

    if (hasFormChanges && !validateForm()) {
      return;
    }

    if (!voucherCode) {
      setSubmitError('교환권 코드가 없습니다.');
      return;
    }

    const projectCode = getProjectCode();
    if (!projectCode) {
      setSubmitError('프로젝트 정보를 찾을 수 없습니다.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    let formSaved = false;

    try {
      if (hasFormChanges) {
        const voucherData = {
          brandCode: formData.brandCode?.trim() || undefined,
          productCode: formData.productCode || null,
          masterCode: formData.masterCode,
          status: formData.status,
          type: formData.type,
          name: formData.name,
          price: formData.price,
          balance: formData.balance,
          discountTarget: formData.discountTarget,
          discountType: formData.discountType,
          discountValue: formData.discountValue,
          maxDiscount: formData.maxDiscount,
          minPayment: formData.minPayment,
          validType: formData.validType,
          validPeriod: formData.validType === 0 ? formData.validPeriod : undefined,
          validUntil:
            formData.validType === 1 && String(formData.validUntil || '').trim()
              ? datetimeLocalToIsoUtc(String(formData.validUntil).trim())
              : undefined,
          issueLimit: formData.issueLimit,
          memberLimit: formData.memberLimit,
          comment: formData.comment || undefined,
          description: formData.description || undefined,
          notice: formData.notice || undefined,
        };

        const result = await updateVoucher(projectCode, voucherCode, voucherData);
        if (!result.success) {
          throw new Error(result.message || '교환권 수정에 실패했습니다.');
        }
        setOriginalData({ ...formData });
        formSaved = true;
      }

      if (hasFileChanges) {
        try {
          const hasNewUpload = uploadFiles.length > 0;
          const shouldDelete = pendingImageDelete || (hasNewUpload && originalHadImage);

          if (shouldDelete) {
            await deleteVoucherPrimary(projectCode, voucherCode);
          }

          for (const uploadFile of uploadFiles) {
            await createVoucherFile(
              projectCode,
              voucherCode,
              toVoucherPrimaryFileRequest(uploadFile)
            );
          }

          setUploadFiles([]);
          setPendingImageDelete(false);
          if (hasNewUpload) {
            setOriginalHadImage(true);
            setExistingImageUrl(null);
            setOriginalExistingImageUrl(null);
          } else {
            setOriginalHadImage(false);
            setImagePreview('');
            setExistingImageUrl(null);
            setOriginalExistingImageUrl(null);
          }
        } catch (fileError) {
          if (formSaved) {
            alert(
              '교환권 정보는 저장되었습니다.\n이미지 파일 처리에 실패했습니다. 다시 시도해 주세요.'
            );
            return;
          }
          throw fileError;
        }
      }

      const searchParams = new URLSearchParams(location.search);
      const page = searchParams.get('page') || '0';
      const size = searchParams.get('size') || '20';
      navigate(`/vouchers?page=${page}&size=${size}`);
    } catch (error: unknown) {
      console.error('교환권 수정 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : CommonUtils.getNetworkErrorMessage(error) || '교환권 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form to original data
  const handleReset = () => {
    if (originalData) {
      setFormData({ ...originalData });
    }
    setUploadFiles([]);
    setImagePreview('');
    setExistingImageUrl(originalExistingImageUrl);
    setPendingImageDelete(false);
    setOriginalHadImage(!!originalExistingImageUrl);
    setImageError('');
    setErrors({});
    setShowResetConfirm(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">교환권 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">오류 발생</h3>
            <p className="text-red-700 mb-4">{loadError}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
              <button
                onClick={() => navigate('/vouchers')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar 
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail || EMPTY_PROJECT_DETAIL}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar 
          title="교환권 수정" 
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail || undefined}
        />

        {/* Edit Voucher Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button and Status */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => navigate('/vouchers')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                교환권 목록으로
              </button>
              
              {hasChanges && (
                <span className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  수정사항 있음
                </span>
              )}
            </div>

            {/* Voucher Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-2">
                    <span>{formData.name} ({formData.masterCode})</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                    {voucherMetadata && (
                      <>
                        <div className="flex items-center">
                          <History className="w-4 h-4 mr-2" />
                          생성: {formatDateTimeFromApi(voucherMetadata.createdAt) || voucherMetadata.createdAt} ({voucherMetadata.createdBy})
                        </div>
                        <div className="flex items-center">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          수정: {formatDateTimeFromApi(voucherMetadata.updatedAt) || voucherMetadata.updatedAt} ({voucherMetadata.updatedBy})
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                <span className="text-red-700">{submitError}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* 교환권 미리보기 및 안내사항 (생성 화면과 동일) */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center mb-4">
                  <Gift className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">교환권 미리보기</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 미리보기 */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">미리보기</h3>
                    <VoucherPreviewCard
                      title={formData.name}
                      benefitText={formatVoucherPreviewBenefit(formData)}
                      validityText={formatVoucherPreviewValidity(formData)}
                      imageUrl={displayImageUrl}
                      isUploading={isImageUploading}
                      error={imageError}
                      onValidatedImage={handleValidatedImage}
                      onRemoveImage={handleRemoveImage}
                      onImageError={setImageError}
                      onImageLoadError={handleImageLoadError}
                    />
                  </div>

                  {/* 안내사항 */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">교환권 생성 도움말</h3>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                        <div>
                          <ul className="text-sm text-blue-800 space-y-1">
                            <li>• 교환권 코드는 고유해야 합니다</li>
                            {/* <li>• 판매 금액·잔액은 0원으로 전송됩니다</li> */}
                            <li>• 이벤트 교환권은 멤버 제한 설정이 필요합니다</li>
                            <li>• 유효기간은 기간 또는 지정일로 설정 가능합니다</li>
                            <li>• 할인 정보는 쿠폰·이벤트 타입에 적용됩니다</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 기본 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center mb-6">
                  <Info className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">기본 정보</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        const nbsp = '\u00A0';
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        return (
                          <option key={brand.code} value={brand.code}>
                            {`${indent}${prefix}${brand.name} (${brand.code})`}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.brandCode}</p>
                    )}
                  </div>

                  {/* 카테고리: treeCodes 브레드크럼 + 삭제 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      카테고리
                    </label>
                    {isLoadingCategoryGroups ? (
                      <div className="flex items-center min-h-[2.5rem]">
                        <p className="text-sm text-gray-500">카테고리 정보 로딩중</p>
                      </div>
                    ) : categoryBreadcrumbs.length === 0 ? (
                      <div className="flex items-center min-h-[2.5rem]">
                        <p className="text-sm text-gray-500">소속된 카테고리/그룹이 없습니다.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {categoryBreadcrumbs.map((item) => (
                          <div
                            key={item.treeCode}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-100 text-gray-800 text-sm"
                          >
                            <span className="flex-1 min-w-0">
                              {item.pathTitles.join(' > ')}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFromTree(item.treeCode)}
                              disabled={removingTreeCode === item.treeCode}
                              className="p-0.5 rounded hover:bg-gray-200 text-gray-500 hover:text-red-600 disabled:opacity-50 flex-shrink-0"
                              title="이 카테고리/그룹에서 제거"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 코드
                    </label>
                    <input
                      type="text"
                      name="masterCode"
                      value={formData.masterCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      title="영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 입력 가능"
                      className={`w-full px-3 py-2 border ${errors.masterCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="영문, 숫자, _, - 만 입력"
                    />
                    {errors.masterCode && <p className="mt-1 text-xs text-red-500">{errors.masterCode}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품 기준 코드
                    </label>
                    <input
                      type="text"
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.productCode ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="상품 기준 코드를 입력하세요"
                      maxLength={20}
                    />
                    {errors.productCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.productCode}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.status ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                    </select>
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.type ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="PREPAID" hidden>
                        선불권
                      </option>
                      <option value="COUPON">쿠폰</option>
                      <option value="BIZ" hidden>
                        기업용
                      </option>
                      <option value="EVENT">이벤트</option>
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 명칭 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="교환권 명칭을 입력하세요"
                      maxLength={20}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 가격 정보 — 당분간 숨김 (기본값 price/balance 폼 state 유지) */}
              <div className="hidden bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  가격 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      판매 금액 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.price ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                    {errors.price && (
                      <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      사용 가능 잔액 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="balance"
                      value={formData.balance}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.balance ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                    {errors.balance && (
                      <p className="mt-1 text-sm text-red-600">{errors.balance}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 할인 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Percent className="w-5 h-5 mr-2" />
                  할인 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      할인 대상 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="discountTarget"
                      value={formData.discountTarget}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.discountTarget ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="PRODUCT">상품</option>
                      <option value="PAYMENT">결제</option>
                      <option value="REWARD">리워드</option>
                    </select>
                    {errors.discountTarget && (
                      <p className="mt-1 text-sm text-red-600">{errors.discountTarget}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      할인 방식 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.discountType ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="AMOUNT">정액</option>
                      <option value="PERCENTAGE">정률</option>
                    </select>
                    {errors.discountType && (
                      <p className="mt-1 text-sm text-red-600">{errors.discountType}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      할인 금액 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="discountValue"
                      value={formData.discountValue}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.discountValue ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                    {errors.discountValue && (
                      <p className="mt-1 text-sm text-red-600">{errors.discountValue}</p>
                    )}
                  </div>

                  {formData.discountType === 'PERCENTAGE' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        최대 할인 금액
                      </label>
                      <input
                        type="number"
                        name="maxDiscount"
                        value={formData.maxDiscount}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.maxDiscount ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="0 (제한없음)"
                        min="0"
                        step="0.01"
                      />
                      {errors.maxDiscount && (
                        <p className="mt-1 text-sm text-red-600">{errors.maxDiscount}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      최소 결제 금액
                    </label>
                    <input
                      type="number"
                      name="minPayment"
                      value={formData.minPayment}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.minPayment ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0 (제한없음)"
                      min="0"
                      step="0.01"
                    />
                    {errors.minPayment && (
                      <p className="mt-1 text-sm text-red-600">{errors.minPayment}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 유효기간 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  유효기간 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      유효기간 타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="validType"
                      value={formData.validType}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.validType ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value={0}>기간</option>
                      <option value={1}>지정일</option>
                    </select>
                    {errors.validType && (
                      <p className="mt-1 text-sm text-red-600">{errors.validType}</p>
                    )}
                  </div>

                  {Number(formData.validType) === 0 ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        유효기간(일) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="validPeriod"
                        value={formData.validPeriod}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.validPeriod ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="0 (무한)"
                        min="0"
                      />
                      {errors.validPeriod && (
                        <p className="mt-1 text-sm text-red-600">{errors.validPeriod}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        유효 일시 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        name="validUntil"
                        step={1}
                        value={formData.validUntil}
                        onChange={handleInputChange}
                        className={`w-full min-w-[200px] px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.validUntil ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.validUntil && (
                        <p className="mt-1 text-sm text-red-600">{errors.validUntil}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 발행 제한 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  발행 제한 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      발행 쿠폰 수 제한 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="issueLimit"
                      value={formData.issueLimit}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.issueLimit ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0 (제한없음)"
                      min="0"
                    />
                    {errors.issueLimit && (
                      <p className="mt-1 text-sm text-red-600">{errors.issueLimit}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      멤버 당 발급 제한
                    </label>
                    <input
                      type="number"
                      name="memberLimit"
                      value={formData.memberLimit}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.memberLimit ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0 (제한없음)"
                      min="0"
                    />
                    {errors.memberLimit && (
                      <p className="mt-1 text-sm text-red-600">{errors.memberLimit}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 설명 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  설명 정보
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      짧은 소개
                    </label>
                    <input
                      type="text"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.comment ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="짧은 소개를 입력하세요"
                      maxLength={50}
                    />
                    {errors.comment && (
                      <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 상세
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.description ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="교환권 상세 설명을 입력하세요"
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 유의사항
                    </label>
                    <textarea
                      name="notice"
                      value={formData.notice}
                      onChange={handleInputChange}
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.notice ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="교환권 유의사항을 입력하세요"
                    />
                    {errors.notice && (
                      <p className="mt-1 text-sm text-red-600">{errors.notice}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center ${!hasChanges && 'opacity-50 cursor-not-allowed'}`}
                  disabled={!hasChanges}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center ${(!hasChanges || isSubmitting || isImageUploading) && 'opacity-50 cursor-not-allowed'}`}
                    disabled={!hasChanges || isSubmitting || isImageUploading}
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        저장 중...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        변경사항 저장
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-amber-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">변경사항 초기화</h3>
            </div>
            <p className="text-gray-600 mb-6">
              저장되지 않은 모든 변경사항이 초기화됩니다. 계속하시겠습니까?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherUpdate;
